import React, { useEffect, useState } from 'react';
import Styled from 'styled-components';

import {
  Card,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { LocalShipping } from '@material-ui/icons';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Close';

import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import CreateEditProductReleaseDialog from '../dialogs/CreateEditProductReleaseDialog';
import {
  useDeleteProductRelease,
  useFindProductReleases
} from '../Product.hooks';
import { Product, ProductRelease } from '../Product.interface';
import { AppSnackbar } from '@sierralabs/react-material-ui';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';

const StyledAction = Styled.div`
  flex: 1;
  text-align: right;
`;

const ProductReleaseCard: React.FC<{ product: Product }> = ({ product }) => {
  const { id: productId } = product;
  const [
    openCreateEditDialog,
    setOpenCreateEditDialog
  ] = useState<ProductRelease | null>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<ProductRelease>();
  const {
    productReleases,
    isLoading,
    loadingError,
    findProductReleases
  } = useFindProductReleases(productId, true);
  const {
    deletedRelease,
    deletingRelease,
    deleteReleaseError,
    deleteRelease
  } = useDeleteProductRelease(productId, openDeleteDialog?.id || 0);
  useEffect(() => {
    if (deletedRelease) {
      findProductReleases();
    }
  }, [deletedRelease, findProductReleases]);
  const { isFieldDisabled } = useTaskEntityFields(product.task);
  return (
    <Card>
      <CardTitle>
        <LocalShipping />
        Releases
        <StyledAction>
          <IconButton
            size='small'
            disabled={isFieldDisabled('releases')}
            onClick={() => setOpenCreateEditDialog(null)}
          >
            <AddIcon />
          </IconButton>
        </StyledAction>
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(isLoading || deletingRelease) && (
              <TableRow>
                <TableCell colSpan={3} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productReleases?.map(release => (
              <TableRow
                hover={!isFieldDisabled('releases')}
                key={release.id}
                onClick={() => {
                  if (!isFieldDisabled('releases')) {
                    setOpenCreateEditDialog(release);
                  }
                }}
              >
                <TableCell>{release.name}</TableCell>
                <TableCell>{release.description}</TableCell>
                <TableCell align='right'>
                  <IconButton
                    size='small'
                    disabled={isFieldDisabled('releases')}
                    onClick={event => {
                      event.stopPropagation();
                      setOpenDeleteDialog(release);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {productReleases?.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>No Product Releases</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {(openCreateEditDialog || openCreateEditDialog === null) && (
        <CreateEditProductReleaseDialog
          open
          productId={productId}
          productRelease={openCreateEditDialog}
          onCancel={() => {
            setOpenCreateEditDialog(undefined);
          }}
          onSubmit={() => {
            setOpenCreateEditDialog(undefined);
            findProductReleases(); // refresh data
          }}
        />
      )}
      {loadingError && (
        <AppSnackbar severity='error' open>
          Error loading releases: {loadingError.message}
        </AppSnackbar>
      )}
      {deleteReleaseError && (
        <AppSnackbar severity='error' open>
          Error deleting release: {deleteReleaseError.message}
        </AppSnackbar>
      )}
      {openDeleteDialog && (
        <ConfirmDialog
          open
          title='Delete Confirm'
          message={`Are you sure you want to remove '${openDeleteDialog?.name}' from the product component?`}
          onCancel={() => {
            setOpenDeleteDialog(undefined);
          }}
          onConfirm={() => {
            setOpenDeleteDialog(undefined);
            deleteRelease();
          }}
        />
      )}
    </Card>
  );
};

export default ProductReleaseCard;
